import type { ForecastContextSelectedOptions } from "@appretio/context-selector";

/** Values a context field currently offers, as the option queries return them. */
export type AvailableContextOptions = Record<
  string,
  string[] | null | undefined
>;

/**
 * Drops selected values a field no longer offers — the state equivalent
 * of the user deselecting them by hand.
 *
 * ``selected`` is returned unchanged when there is nothing to prune, so
 * feeding the result into a ``setState`` bails out instead of
 * re-rendering.
 *
 * Fields whose available list is missing or empty are left alone: option
 * lists arrive asynchronously and refetch on every version / filter
 * change, so "no values yet" must never be read as "nothing on offer".
 */
export function pruneStaleContextSelections(
  selected: ForecastContextSelectedOptions,
  availableByField: AvailableContextOptions
): ForecastContextSelectedOptions {
  if (!selected) return selected;

  let pruned: Record<string, string[]> | undefined;

  for (const [field, values] of Object.entries(selected)) {
    const available = availableByField[field];
    if (!values?.length || !available?.length) continue;

    const kept = values.filter((value) => available.includes(value));
    if (kept.length === values.length) continue;

    pruned ??= { ...selected };
    // Matches ContextSelector, which removes the key rather than keeping
    // an empty array when the user clears a field's last value.
    if (kept.length === 0) delete pruned[field];
    else pruned[field] = kept;
  }

  return pruned ?? selected;
}
